import { store } from '../store/store';
import { apiHub } from '../modules/api-hub';
import * as helpers from '../modules/helpers';
import AuthToken from "../authToken";
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';


// add withCredentials to every request
axios.interceptors.request.use((config) => {
    // https://regex101.com/r/4ogUPb/1
    const re = /^https:\/\/api(|-uat1|-uat3|-dit3|-perf).slsp.sk\//;
    if(re.test(config.url)) {
        config.withCredentials = true;
    }
    return config;
}, (error) => {
    return Promise.reject(error);
});

/**
 * Decide enviroment from env string and set it to store.
 *
 * @param envString - Usually URL
 */
export const decideEnvironment = (envString) => {
    let url = '';

    if (import.meta.env.VITE_BASE_API_URL) {
        url = import.meta.env.VITE_BASE_API_URL;
    } else {
        const envList = apiHub.env.list;

        for(let i = 0; i < envList.length; i++) {

            if (envString.includes(envList[i])) {
                url = apiHub.env.urls[envList[i]];
                break;
            }
        }
    }

    if (!url) {
        url = apiHub.env.urls.prod;
    }

    store.dispatch('setOne', {key: 'apiUrl', value: url});
}

/**
 * Request our node server to retrieve
 * merchantCheck request payload.
 *
 * @param id - Data ID
 * @returns Promise with response
 */
export const getMcParams = (id) => {
    // Get constants from store.
    const url = apiHub.getMcParams.apiUrl;
    const sid = store.getters.getOne('sid');

    return new Promise((resolve, reject) => {

        // If token is invalidated use this handbrake.
        if (store.getters.getOne('invalidated')) { resolve({error: 'apiCallPrevented'}); return false; }

        axios({
            method: apiHub.getMcParams.method,
            url,
            params: {
                id: uuidv4(),
            },
            headers: {
                Authorization: AuthToken.value,
                'data-id': id,
                sid,
            }
        })
        .then((response) => {
            resolve(response);
        })
        .catch((error) => {
            reject(error);
        });
    });
}

/**
 * Retrieve SecV url.
 *
 * @returns Promise with response
 */
export const login = () => {
    // Get constants from store.
    const url = apiHub.login.apiUrl;

    return new Promise((resolve, reject) => {

        // If token is invalidated use this handbrake.
        if (store.getters.getOne('invalidated')) { resolve({error: 'apiCallPrevented'}); return false; }

        axios({
            method: apiHub.login.method,
            url,
            headers: {
                Authorization: AuthToken.value
            }
        })
        .then((response) => {
            resolve(response);
        })
        .catch((error) => {
            reject(error);
        });
    });
}

/**
 * Request our node server to store
 * redirect url with other information.
 *
 * @param data - Data to log
 * @returns Promise with response
 */
export const log = (data) => {
    // Get constants from store.
    const authToken = AuthToken.value || '2TJYJAXRT2JTP3VVBWQPK2XBIUE2CUAX';
    const url = apiHub.log.apiUrl;
    const sid = store.getters.getOne('sid');

    return new Promise((resolve) => {
        // If token is invalidated use this handbrake.
        if (store.getters.getOne('invalidated')) { resolve({error: 'apiCallPrevented'}); return false; }
        axios({
            method: apiHub.log.method,
            url,
            headers: {
                Authorization: authToken,
                sid,
            },
            data
        })
        .then((response) => {
            resolve(response);
        })
        .catch((error) => {
            resolve(error);
        });
    });
}

/**
 * Merchant check API call.
 *
 * @param data - Data to send
 * @returns Promise with response
 */
export const merchantCheck = (data) => {
    // Get constants from store.
    const apiUrl = store.getters.getOne('apiUrl');
    const url = apiUrl + apiHub.merchantCheck.apiUrl;

    return new Promise((resolve) => {

        // If token is invalidated use this handbrake.
        if (store.getters.getOne('invalidated')) { resolve({error: 'apiCallPrevented'}); return false; }

        axios({
            method: apiHub.merchantCheck.method,
            url,
            headers: {
                Authorization: AuthToken.value,
            },
            data
        })
        .then((response) => {
            resolve(response);
        })
        .catch((error) => {
            resolve(handleChfErrors(error));
        });
    });
};

/**
 * mToken polling method.
 *
 * @param data - Polling url and polling interval
 * @param ctx - Calling component
 */
export const mToken = async ({url, interval}, ctx) => {
    const finalUrl = `${store.getters.getOne('apiUrl')}api${url}`;

    ctx.active = true;
    let lastCallTime = 0;

    store.dispatch('setOne', {key: 'mTokenRefresh', value: setInterval(async () => {

        if (ctx.submitingAuthKey) { return false; }

        // prevent calling poll in a quick succession
        // (there is a weird behaviour, when sporopay is put to idle state while user is signing the payment with George ID and after return to sporopay it fires all the missed polls at once)
        if ((Date.now() - lastCallTime) < 1000) { return false; }

        lastCallTime = Date.now();

        axios({
            method: 'GET',
            url: finalUrl,
            headers: {
                Authorization: AuthToken.value,
                "Content-Type": "application/json"
            },
        })
        .then(async (response) => {

            // This happens if user clicks back during polling.
            if (ctx.method === '') {
                clearInterval(store.getters.getOne('mTokenRefresh'));
                return false;
            }

            // prevent handling poll if user is leaving the page (most likely the payment is signed already)
            if (store.getters.getOne('leaveInProcess')) { return false; }

            switch (response.data.state) {
                case 'PROCESSING':
                    break;
                case 'DONE':
                    store.dispatch('setOne', {key: 'leaveInProcess', value: true});
                    clearInterval(store.getters.getOne('mTokenRefresh'));
                    ctx.errorMsg = {};
                    await ctx.paymentCheck();
                    break;
                case 'REJECTED':
                    ctx.$parent.die('REJECTED', false, {translate: true, title: 'modals.failed.title', caption: 'sign.errors.mTokenFailedError' });
                    break;
                case 'FALLBACK':
                    clearInterval(store.getters.getOne('mTokenRefresh'));
                    ctx.$parent.die('FALLBACK', false);
                    break;
                case 'BLOCKED_INVESTIGATION':
                    ctx.$parent.die('BLOCKED_INVESTIGATION', true, {translate: true, title: 'modals.blockedInvestigation.title', caption: 'modals.blockedInvestigation.caption' });
                    break;
                case 'BLOCKED_FRAUD':
                    ctx.$parent.die('BLOCKED_FRAUD', true, {translate: true, title: 'modals.blockedFraud.title', caption: 'modals.blockedFraud.caption' });
                    break;
                case 'NOT_SIGNABLE':
                    clearInterval(store.getters.getOne('mTokenRefresh'));
                    ctx.$parent.die('NOT_SIGNABLE', false, {translate: true, title: 'modals.notSignable.title', caption: 'modals.notSignable.caption' });
                    break;
                default:
                    clearInterval(store.getters.getOne('mTokenRefresh'));
                    ctx.$parent.die('Incorrect polling status code.', false);
                    break;
            };

        })
        .catch((error) => {
            let errorId;

            try {
                errorId = JSON.parse(error.message)?.errorId;
            } finally {
                if (!errorId) ctx.$parent.die(error.message, false);
            }
        });

    }, interval) });
};

/**
 * Get available source accounts.
 *
 * @returns Promise with response
 */
export const accounts = () => {
    // Get constants from store.
    const apiUrl = store.getters.getOne('apiUrl');
    const url = apiUrl + apiHub.accounts.apiUrl;

    return new Promise((resolve) => {

        // If token is invalidated use this handbrake.
        if (store.getters.getOne('invalidated')) { resolve({error: 'apiCallPrevented'}); return false; }

        axios({
            method: apiHub.accounts.method,
            url,
            headers: {
                Authorization: AuthToken.value
            }
        })
        .then((response) => {
            resolve(response);
        })
        .catch((error) => {
            resolve(handleChfErrors(error));
        });
    });
}

/**
 * Function used for token invalidation.
 *
 * @returns Promise with response
 */
export const invalidateToken = () => {
    // Invalidate only when token is valid.
    if (store.getters.getOne('invalidated')) { return false; }

    // Get constants from store.
    const apiUrl = store.getters.getOne('apiUrl');
    const url = apiUrl + apiHub.invalidateToken.apiUrl;
    let authToken = AuthToken.value;

    // Set this status to prevent further API calls.
    store.dispatch('setOne', {key: 'invalidated', value: true});

    // Sometimes we have to fetch token directly from url and clear it. This happens because
    // CSRF validation is ran earlier than parameters processing and token is not yet loaded
    // in internal storage and nor parameters are touched (removed from url). I suppose it is
    // good practice to let this function clear things up if it determines it is needed, because
    // this function is terminal.

    if (!authToken) {
        authToken = helpers.getHash('access_token');
        history.pushState('', document.title, window.location.pathname);
    }

    return new Promise((resolve) => {
        axios({
            method: apiHub.invalidateToken.method,
            url,
            headers: {
                Authorization: authToken,
                'Content-Type': 'application/json',
            },
        })
        .then((response) => {
            resolve(response.data);
        })
        .catch((error) => {
            const errResponse = handleChfErrors(error);
            resolve(errResponse);
        });
    });
};

/**
 * Function used for token refreshing.
 *
 * @param seconds - Amount of refreshing seconds
 */
export const refreshToken = (seconds) => {
    // Get constants from store.
    const apiUrl = store.getters.getOne('apiUrl');
    const url = apiUrl + apiHub.refreshToken.apiUrl;

    store.dispatch('setOne', {key: 'refreshToken', value: setInterval(() => {
        axios({
            method: apiHub.refreshToken.method,
            url,
            headers: {
                Authorization: AuthToken.value,
                'Content-Type': 'application/json',
            },
        });
    }, seconds * 1000) });
};

/**
 * Function returns signId needed for signing process start.
 *
 * @oaram data - Data to send
 * @returns Promise with response
 */
export const getSignId = (data) => {
    // Get constants from store.
    const apiUrl = store.getters.getOne('apiUrl');
    const url = apiUrl + apiHub.getSignId.apiUrl;

    // Remove unused optional fields.
    helpers.filterKeys(data, '');

    return new Promise((resolve) => {

        // If token is invalidated use this handbrake.
        if (store.getters.getOne('invalidated')) { resolve({error: 'apiCallPrevented'}); return false; }

        axios({
            method: apiHub.getSignId.method,
            url,
            headers: {
                Authorization: AuthToken.value,
                'Content-Type': 'application/json',
            },
            data
        })
        .then((response) => {
            resolve(response.data);
        })
        .catch((error) => {
            const errResponse = handleChfErrors(error);
            resolve(errResponse);
        });
    });
};

/**
 * Function deletes sign request. This happens when user return from signing screen to any previous, or close app.
 *
 * @returns Promise with response
 */
export const deleteSignRequest = () => {
    // get constants from store.
    const apiUrl = store.getters.getOne('apiUrl');
    const signId = store.getters.getOne('signId');
    const url = apiUrl + helpers.parse(apiHub.deleteSignRequest.apiUrl, signId);

    return new Promise((resolve) => {

        // If token is invalidated use this handbrake.
        if (store.getters.getOne('invalidated')) { resolve({error: 'apiCallPrevented'}); return false; }

        // Do not do api call if we dont have sign id.
        if (signId !== '') {
            axios({
                method: apiHub.deleteSignRequest.method,
                url,
                headers: {
                    Authorization: AuthToken.value,
                }
            })
            .then(() => {
                resolve(true);
            })
            .catch((error) => {
                const errResponse = handleChfErrors(error);
                resolve(errResponse);
            });
        } else {
            resolve(true);
        }

    });
};

/**
 * Function checks the status of transaction.
 *
 * @returns Promise with response
 */
export const paymentCheck = async () => {
    // Get constants from store.
    const apiUrl = store.getters.getOne('apiUrl');
    const signId = store.getters.getOne('signId');
    const url = apiUrl + helpers.parse(apiHub.paymentCheck.apiUrl, signId);
    const isSepa = store.getters.getOne('isSepa');

    if (store.getters.getOne('invalidated')) { return {error: 'apiCallPrevented'}; }


        // If token is invalidated use this handbrake.

        try {
            const response = await axios({
                method: apiHub.paymentCheck.method,
                url,
                headers: {
                    Authorization: AuthToken.value
                },
                params: {
                    isSepa
                }
            });
            return response.data;
        } catch (error) {
            const errResponse = handleChfErrors(error);
            return errResponse;
        }
};

/**
 * Fetches signing methods.
 *
 * @param transactionId - ID of transaction
 * @returns Promise with response
 */
export const signingStep1 = (transactionId) => {
    // Get constants from store.
    const apiUrl = store.getters.getOne('apiUrl');

    // Get url.
    const url = apiUrl + helpers.parse(apiHub.signingStep1.apiUrl, transactionId);

    return new Promise((resolve) => {

        // If token is invalidated use this handbrake.
        if (store.getters.getOne('invalidated')) { resolve({error: 'apiCallPrevented'}); return false; }

        axios({
            method: apiHub.signingStep1.method,
            url,
            headers: {
                Authorization: AuthToken.value,
            }
        })
        .then((response) => {
            resolve(response.data);
        })
        .catch((error) => {
            const errResponse = handleChfErrors(error);
            resolve(errResponse);
        });
    });
};

/**
 * Sends back selected auth metod
 *
 * @param method - Signing method identifier
 * @returns Promise with response
 */
export const signingStep2 = (method) => {
    // Get constants from store.
    const apiUrl = store.getters.getOne('apiUrl');
    const signId = store.getters.getOne('signId');
    const url = apiUrl + helpers.parse(apiHub.signingStep2.apiUrl, signId);

    // Prepare data.
    const data = JSON.parse(helpers.parse(JSON.stringify(apiHub.signingStep2.messageBody), method));

    return new Promise((resolve) => {

        // If token is invalidated use this handbrake.
        if (store.getters.getOne('invalidated')) { resolve({error: 'apiCallPrevented'}); return false; }

        axios({
            method: apiHub.signingStep2.method,
            url,
            headers: {
                Authorization: AuthToken.value,
            },
            data
        })
        .then((response) => {
            resolve(response.data);
        })
        .catch((error) => {
            const errResponse = handleChfErrors(error);
            resolve(errResponse);
        });
    });
};

/**
 * Sends back selected auth metod
 *
 * @param data - Data to send
 * @returns Promise with response
 */
export const signingStep3 = (data) => {
    // Get constants from store.
    const apiUrl = store.getters.getOne('apiUrl');
    const signId = store.getters.getOne('signId');

    // Get url.
    const url = apiUrl + helpers.parse(apiHub.signingStep3.apiUrl, signId);

    return new Promise((resolve) => {

        // If token is invalidated use this handbrake.
        if (store.getters.getOne('signing') || store.getters.getOne('invalidated')) { resolve({error: 'apiCallPrevented'}); return false; }

        // Prevent double signing call.
        store.dispatch('setOne', {key: 'signing', value: true});

        axios({
            method: apiHub.signingStep3.method,
            url,
            headers: {
                Authorization: AuthToken.value
            },
            data: JSON.parse(data)
        })
        .then((response) => {
            resolve(response.data);
        })
        .catch((error) => {
            const errResponse = handleChfErrors(error);
            resolve(errResponse);
        })
        .finally((data) => {
            store.dispatch('setOne', {key: 'signing', value: false});
        });
    });
};

/**
 * Enable visuals after they've been blurred during signing. This function is here because
 * handleChfErrors method is used in every single API call and secures that screen will
 * be enabled during any error.
 */
const visualEnabler = () => {
    document.body.style.opacity = 1;
    document.body.style.pointerEvents = 'auto';
}

/**
 * Function handles all kinds of errors than can occur during API calls.
 *
 * @param error - CHF error object node
 * @returns Error object
 */
const handleChfErrors = (error) => {

    clearInterval(store.getters.getOne('refreshToken'));

    const responseStatus = error.response.status;
    const responseData = error.response.data;

    visualEnabler();

    if (responseStatus === 400 || responseStatus === 403) {

        // Internal errors.
        // @hook{internalerror}
        if (typeof responseData.errors === "undefined") {
            return {
                error: 'internal'
            }


        // Token errors (not found, invalid, not allowed, expired, sso switch).
        // @hook{tokenerror}
        } else if (responseData.errors[0].scope === "token") {
            return {
                error: 'invalidToken'
            }


        // Payment API errors.
        // @hook{paymenterror}
        } else if (responseData.errors[0].error === 'SK-ORDER_BLOCKED_FPS') {
            return {
                error: 'skOrderBlockedFps',
            }

        } else if (responseData.errors[0].error === "OPERATION_NOT_ALLOWED" && responseData.errors[0].scope === "account") {
            return {
                error: 'invalidPhoneNumber'
            }


        // Payment check API errors.
        // @hook{paymentcheckerror}
        } else if (responseData.errors[0].error === "OPERATION_NOT_ALLOWED" && responseData.errors[0].scope === "operation") {
            return {
                error: 'invalidOperation'
            }


        // General API errors.
        // @hook{generalerror}
        } else if (responseData.errors[0].error === "NO_AUTH_TOOLS_FOR_ACCOUNT") {
            return {
                error: 'noAuthTools'
            }
        } else if (responseData.errors[0].error === "SESSION_INVALID") {
            return {
                error: 'invalidSession'
            }
        } else if (responseData.errors[0].error === "OPERATION_NOT_ALLOWED" && responseData.errors[0].scope === "merchant") {
            return {
                error: 'invalidMerchant'
            }
        } else if (responseData.errors[0].error === "OPERATION_NOT_ALLOWED" && responseData.errors[0].scope === "ID") {
            return {
                error: 'invalidId'
            }
        } else if (responseData.errors[0].error === "FIELD_INVALID" && responseData.errors[0].scope === "variableSymbol") {
            return {
                error: 'invalidVs'
            }
        } else if (responseData.errors[0].error === "FIELD_INVALID" && responseData.errors[0].scope === "specificSymbol") {
            return {
                error: 'invalidSs'
            }
        } else if (responseData.errors[0].error === "FIELD_INVALID" && responseData.errors[0].scope === "paymentType") {
            return {
                error: 'invalidPaymentType'
            }
        } else if (responseData.errors[0].error === "OTP_INVALID" && responseData.errors[0].scope === "oneTimePassword") {
            return {
                error: 'otpInvalid'
            }


        // NONE API errors
        // @hook{noneerror}
        } else if (responseData.errors[0].error === "BLOCKED_INVESTIGATION" && responseData.errors[0].scope === "signId") {
            return {
                error: 'blockedInvestigation'
            }
        } else if (responseData.errors[0].error === "BLOCKED_FRAUD" && responseData.errors[0].scope === "signId") {
            return {
                error: 'blockedFraud'
            }
        } else if (responseData.errors[0].error === "AUTH_LIMIT_EXCEEDED" && responseData.errors[0].scope === "signId") {
            return {
                error: 'authLimitExceeded'
            }
        } else if (responseData.errors[0].error === "NO_AUTH_AVAILABLE" && responseData.errors[0].scope === "authorizationType") {
            return {
                error: 'noAuthAvailable'
            }


        // Unspecified API errors.
        // @hook{unspecifiederror}
        } else {
            return {
                error: 'unspecified'
            }
        }

    // Errors with response status different than 400 or 403.
    // @hook{outofintervalstatus}
    } else {
        return {
            error: 'outOfIntervalStatus'
        }
    }
};
